import { Modal, useToastMessage } from 'ccs-ui-components';
import SidebarLoader from 'ccs-ui-components/dist/assets/images/BlueGreenSpinner.gif';
import _ from 'lodash';
import React, {
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react';
import { Dropdown as BDropdown, ButtonGroup, Row } from 'react-bootstrap';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import useConfirmDialog from '../../../common/hooks/ConfirmationDialog';
import useLoader from '../../../common/hooks/Loader';
import { type JourneyDesignerServiceReturnLocationState, consumeServiceUpdatedFlag } from '../../../common/utilities/JourneyDesignerReturnContext';
import FlowBuilder, { FlowBuilderElement } from '../../_components/flow-builder/FlowBuilder';
import JourneyBuilderHeader from '../../_components/journey-builder-header/JourneyBuilderHeader';
import { PayloadTransformationModal } from '../../_components/payload-transformation/PayloadTransformationModal';
import { SchedulerModal } from '../../_components/scheduler/SchedulerModal';
import AllServices from '../../_components/sidebars/all-services/AllServices';
import AssociatedServices from '../../_components/sidebars/associated-services/AssociatedServices';
import CommentHistoryModal from '../../_components/sidebars/comment-history-modal/CommentHistoryModal';
import IngressEvents from '../../_components/sidebars/ingress-events/IngressEvents';
import { FLOW_CONFIG, initialState, journeyCanvasConstants } from '../../constants/JourneyCanvas';
import { getAllServices, getJourney, getServiceByVersionAndName, saveJourney } from '../../store/_api';
import { AssociatedService, CreateJourneyData, CreateJourneyStepDetails, CreateJourneyStepEdge, CreateJourneyStepNode, IngressEvent, Service, ServiceOperation, ViewJourneyData } from '../../store/_api/TypeDefs';
import ErrorFallBack from './ErrorFallback';
import { reducer } from './reducer/JourneyCanvasReducer';
import { Actions } from './reducer/TypeDefs';
import { DraggingItemType, EventMapDetails, FlowItemButtonActions, FlowItemConditions, PayloadTransformation, SidebarDataLoadingState, SidebarState } from './utility/TypeDefs';

import CemHeader from '../../_components/cem-header/CemHeader';
import { JourneyBuilderFooter } from '../../_components/journey-builder-footer/JourneyBuilderFooter';
import { ActiveStatusEditWarningModal, ApproveWarningModal, CemCancelWarningModal, ConfirmDeleteModal, ConfirmErrorModal, ExternalOnSubsequentStepModal, JourneyStepDetailsModal, MoveToDraftModal, ReturnJourneyModal, SaveJourneyToDesignCemModal, SendForApproveModal } from '../../_components/journey-canvas-modals/JourneyCanvasModals';
import { buildJourneyCanvasUrl, journeyBuilderRoutes } from '../../constants/JdRoutes';
import classes from './JourneyCanvas.module.scss';
import {
  cancelActiveStatusEditWarningModal, cancelCEMHandler, closeSaveJourneyModal, closeStepModalHandler, closeTransformPayloadModal, computeIsCompensateAction, conditionSelectHandler, confirmDelete, confirmErrorHandler, connectorBtnClickHandler, connectorDrawHandler, connectorSelectHandler, createEventMapDetail, createJourneyHandler, createJourneyStepDetails, createJourneyStepEdges, createJourneyStepNodes, deleteCompensatoryEventMapHandler,
  deleteStepCardHandler, dismissStepCardMenuDropdown, dragStartHandler, dropEndHandler, editStepDetailsHandler, egressClickHandler, elementSelectHandler,
  fetchJourneyData, flowItemDragCompleteHandler, fullScreenHandler,
  getSearchParamOrDefault,
  hideDeleteNotifyDialogHandler, hideSidebar, ingressEventClickHandler,
  initiateStepClickHandler, itemButtonClickHandler, leaveCancelWarningModal, loadAssociatedServicesForNode, onEditHandler, onPayloadSaveHandler, prepareJourneyFromData, proceedActiveStatusEditWarningModal, saveCompensatoryEventMap, saveStepModalHandler, serviceFilterHandler, setCloseSchedulerModal, setOpenSchedulerModal, startBtnClickHandler, stepCardMenuClickHandler, updateJourneyHandler
} from './utility/helpers';
import { listClearableCondExprIngressLabelsForMerge, toFlowMergeRefs } from './utility/helpers/AndMergeForkGraph';
import { ExternalEventConfirmationModal } from '../../_components/external-event-confirmation-modal/ExternalEventConfirmationModal';
import EventSchemaSidebar from '../../_components/sidebars/event-schema/EventSchemaSidebar';
import ExpressionBuilderSidebar from '../../_components/sidebars/expression-builder/ExpressionBuilderSidebar';

const JourneyCanvas: React.FC = () => {
  // show/hide Modal for editing of Active status journey
  const [showModalForActiveEdit, setShowModalForActiveEdit] = useState<boolean>(false);
  const [showCancelWarningModal, setShowCancelWarningModal] = useState<boolean>(false);
  const [showApproveWarningModal, setShowApproveWarningModal] = useState<boolean>(false);
  const [showMoveToDraftWarningModal, setShowMoveToDraftWarningModal] = useState<boolean>(false);
  const [showSendForApprovalWarningModal, setShowSendForApprovalWarningModal] = useState<boolean>(false);
  const [showReturnModal, setShowReturnModal] = useState<boolean>(false);
  const [showPayloadInfoModal, setShowPayloadInfoModal] = useState<boolean>(false);
  const [pendingConditionChange, setPendingConditionChange] = useState<{ nodeId: string; conditionText: string } | null>(null);
  const [conditionPopover, setConditionPopover] = useState<{ nodeId: string; x: number; y: number; current: string } | null>(null);
  const [showOrToAndPayloadModal, setShowOrToAndPayloadModal] = useState<boolean>(false);
  const [showOrToAndCondExpModal, setShowOrToAndCondExpModal] = useState<boolean>(false);
  const [pendingOrToAndMergeId, setPendingOrToAndMergeId] = useState<string | null>(null);
  const [pendingCondExpIngressLabels, setPendingCondExpIngressLabels] = useState<string[]>([]);
  const [enableSetForApprovalBtn, setEnableSetForApprovalBtn] = useState<boolean>(true);
  const [summaryStats, setSummaryStats] = useState<{ serviceCount: number, eventCount: number, cemCount: number }>({ serviceCount: 0, eventCount: 0, cemCount: 0 });
  /**
   * To manage and update states
   * dispatch actions based on respective identifiers
   * Using `useReducer` instead of `useState` to optimize performance for components
   */
  const [state, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();
  const { cloneJourneyName = '', cloneJourneyVersion = 0 } = location.state as {
    cloneJourneyName?: string,
    cloneJourneyVersion?: number
  } || {};

  /**
   * To store the ref of div wrapping the flow builder.
   * Size of this div is used to calculate the size of
   * canvas so that it will fill the complete screen
   */
  const canvasRootRef = useRef<HTMLDivElement>(null);
  const flowBuilderRef = useRef<FlowBuilderElement>(null);
  const jdServiceReturnHandledRef = useRef(false);
  const animatedCemRootNodeRef = useRef<string | undefined>(undefined);
  const restoredParentJourneyNodeRef = useRef<string | undefined>(undefined);
  // When returning from Control Plane while a CEM was being edited, the resume can only
  // run after the slide animation finishes and LOAD_COMPENSATORY_EVENT_MAP has populated
  // state.flowItems with the CEM nodes. We park the desired selection here and a separate
  // effect consumes it once the CEM is fully loaded.
  const pendingCemResumeRef = useRef<{ cemRootNodeId: string, selectedFlowItemNodeId?: string } | undefined>(undefined);
  const pendingFlowItemFocusRef = useRef<string | undefined>(undefined);
  // Guards URL-driven step entry so we don't re-dispatch CLICK_STEP_CARD on every render.
  const stepUrlResumeHandledRef = useRef<string | undefined>(undefined);
  // When set, the next designingStep / designingCEM transition will NOT reset the canvas
  // translation to (0,0). Used by the Control-Plane return flow so we can position the
  // viewport on the previously-selected flow item instead of snapping back to the origin.
  const skipTranslationResetRef = useRef(false);

  const resetJourneyCardsViewport = () => {
    // Defer until FlowBuilder has re-rendered with journey step cards (not step-flow items).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        flowBuilderRef.current?.resetJourneyCardsViewport(
          state.stepItems,
          state.initiateStepCardVisible,
        );
      });
    });
  };

  const stepDataRef = useRef<EventMapDetails>(undefined);
  const journeyDataRef = useRef<{ nodes: CreateJourneyStepNode[], edges: CreateJourneyStepEdge[], stepDetails: CreateJourneyStepDetails }>(undefined);

  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const toastMessage = useToastMessage();
  const loader = useLoader();
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const confirmDialog = useConfirmDialog();

  // get the url parameters
  const { id: journeyId = '' } = useParams<{ id: string }>();
  const { mode = '' } = useParams<{ mode: string }>();
  const { version = '' } = useParams<{ version: string }>();
  const { stepName: rawStepNameParam } = useParams<{ stepName: string }>();
  const stepNameFromUrl = rawStepNameParam ? decodeURIComponent(rawStepNameParam) : undefined;
  // get the search URL parameters
  const [searchParams] = useSearchParams();
  const viewJourney = mode === 'view';
  const versionForCurrentJourney = Number(version ?? '1');
  const descriptionForCurrentJourney = getSearchParamOrDefault(searchParams, 'description');
  const groupForCurrentJourney = getSearchParamOrDefault(searchParams, 'group');

  const isCreate = mode === 'create';

  // check if status is active or inactive
  const journeyStatus = state.journeyStatus.toLowerCase();
  const validStatuses = ['active', 'inactive'];
  const isStatusActiveOrInactive = validStatuses.includes(journeyStatus);

  const onCloseExpressionBuilderSidebar = () => {
    dispatch({ type: Actions.HIDE_COND_EXP_MODAL });
    dispatch({ type: Actions.CLEAR_EVENT_SCHEMA });
  };

  useEffect(() => {
    dispatch({
      type: Actions.VIEW_JOURNEY_INITIATED,
      payload: {
        flag: viewJourney,
      }
    });

    /**
     * @method resizeListener
     * @description resizing canvas size wrt screen size
     */
    const resizeCanvasWithScreenSize = () => {
      if (canvasRootRef.current) {
        const { height } = canvasRootRef.current.getBoundingClientRect();
        dispatch({
          type: Actions.FLOW_BUILDER_SIZE,
          payload: {
            width: window.innerWidth - journeyCanvasConstants.canvasMarginAndBorder * 2,
            height: height - journeyCanvasConstants.canvasMarginAndBorder * 2
          }
        });
      }
    };

    // canvas size based on parent size
    resizeCanvasWithScreenSize();

    const resizeListener = () => {
      resizeCanvasWithScreenSize();
    };
    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [viewJourney]);

  useEffect(() => {
    // enable save btn on clone journey
    if (cloneJourneyName && cloneJourneyVersion) {
      dispatch({ type: Actions.SAVE_JOURNEY_BTN_ENABLE, payload: { save: false } });
    }
  }, [cloneJourneyName, cloneJourneyVersion]);

  useEffect(() => {
    if (consumeServiceUpdatedFlag()) {
      toastMessage.success(
        translate('journey_builder_component.title_toast_success'),
        translate('journey_builder_component.msg_service_updated_return')
      );
    }
  }, []);

  useEffect(() => {
    jdServiceReturnHandledRef.current = false;
  }, [journeyId]);

  useEffect(() => {
    const st = location.state as JourneyDesignerServiceReturnLocationState | null;
    const hasMarker = searchParams.get('jdReturn') === '1' || st?.jdServiceReturn;
    if (!hasMarker || jdServiceReturnHandledRef.current) {
      return;
    }
    if (state.viewJourneyInitiated === undefined) {
      return;
    }

    const resume = st?.jdResume ?? { kind: 'none' as const };
    if ((resume.kind === 'step' || resume.kind === 'cem') && !state.stepJourneyDataMap[resume.stepNodeId]) {
      return;
    }

    jdServiceReturnHandledRef.current = true;

    const run = async () => {
      loaderRef.current.show();
      try {
        const fresh = (await getAllServices(0, journeyCanvasConstants.allServicesPageSize, '')) ?? [];
        dispatch({ type: Actions.REPLACE_ALL_SERVICES_LIST, payload: { services: fresh } });

        const sidebarPick = st?.jdResumeSidebar === 'ASSOCIATED_SERVICES'
          ? SidebarState.ASSOCIATED_SERVICES
          : SidebarState.ALL_SERVICES;

        if (resume.kind === 'cem' && state.stepJourneyDataMap[resume.stepNodeId]) {
          // Step 1: re-enter the parent step. After this dispatch state.flowItems will
          // contain the step's nodes (including the CEM root).
          dispatch({ type: Actions.CLICK_STEP_CARD, payload: { nodeId: resume.stepNodeId } });

          // Step 2: open the CEM the same way itemButtonClickHandler does — fetch the
          // saved CEM, build flowItems, then dispatch DESIGN_COMPENSATORY_EVENT_MAP. The
          // slide-animation effect picks up the new cemRootNodeId and, when the slide
          // finishes, dispatches LOAD_COMPENSATORY_EVENT_MAP to populate flowItems with
          // the CEM children.
          //
          // We park the selected-node info in pendingCemResumeRef so a separate effect
          // can wait for LOAD to finish before dispatching SELECT_FLOW_ITEM.
          pendingCemResumeRef.current = {
            cemRootNodeId: resume.cemRootNodeId,
            selectedFlowItemNodeId: resume.selectedFlowItemNodeId,
          };
          await itemButtonClickHandler({
            id: FlowItemButtonActions.EDIT_CEM,
            nodeId: resume.cemRootNodeId,
            loader: loaderRef.current,
            toastMessage,
            translate,
            dispatch,
            flowItemDataMap: state.flowItemDataMap,
            journeyVersion: state.journeyVersion,
            readOnly: !!state.viewJourneyInitiated,
          });
        } else if (resume.kind === 'step' && state.stepJourneyDataMap[resume.stepNodeId]) {
          const selectedNodeId = resume.selectedFlowItemNodeId;
          const selectedItemData = selectedNodeId ? state.flowItemDataMap[selectedNodeId] : undefined;

          if (selectedNodeId) {
            skipTranslationResetRef.current = true;
            pendingFlowItemFocusRef.current = selectedNodeId;
          }

          dispatch({ type: Actions.CLICK_STEP_CARD, payload: { nodeId: resume.stepNodeId } });

          if (selectedNodeId && selectedItemData) {
            dispatch({
              type: Actions.SELECT_FLOW_ITEM,
              payload: { nodeId: selectedNodeId, serviceData: selectedItemData },
            });
            await loadAssociatedServicesForNode(dispatch, state.flowItemDataMap, selectedNodeId, state.cemRootNodeId);
          } else {
            pendingFlowItemFocusRef.current = undefined;
            dispatch({ type: Actions.OPEN_SIDEBAR_AFTER_SERVICE_RETURN, payload: { sidebar: sidebarPick } });
          }
        } else if (resume.kind === 'none') {
          resetJourneyCardsViewport();
        }
      } finally {
        loaderRef.current.hide();
      }

      navigate({ pathname: location.pathname, search: '' }, { replace: true, state: null });
    };

    void run();
  }, [
    dispatch,
    location.pathname,
    location.state,
    navigate,
    searchParams,
    state.cemRootNodeId,
    state.flowBuilderSizeState,
    state.flowItemDataMap,
    state.stepJourneyDataMap,
    state.viewJourneyInitiated,
  ]);

  // CEM-resume tail: when the user is returning from Control Plane while a CEM was being
  // edited, the main return-handler above only gets as far as dispatching
  // DESIGN_COMPENSATORY_EVENT_MAP. The slide animation + LOAD_COMPENSATORY_EVENT_MAP happen
  // ~700ms later and populate state.flowItems with the CEM children. Only after that can
  // we re-select the previously highlighted node and pan the canvas to it. This effect
  // watches for the CEM to finish loading and then consumes the pending resume payload.
  useEffect(() => {
    const pending = pendingCemResumeRef.current;
    if (!pending) {
      return;
    }
    if (!state.designingCEM || state.cemRootNodeId !== pending.cemRootNodeId) {
      return;
    }
    // flowItems[0] is the CEM root copy; CEM children land at flowItems[1..]. Wait until
    // LOAD has populated them so SELECT_FLOW_ITEM has a valid target.
    if (state.flowItems.length <= 1) {
      return;
    }

    const { selectedFlowItemNodeId } = pending;
    pendingCemResumeRef.current = undefined;

    if (!selectedFlowItemNodeId) {
      return;
    }

    const selectedItemData = state.flowItemDataMap[selectedFlowItemNodeId];
    if (!selectedItemData) {
      return;
    }

    dispatch({
      type: Actions.SELECT_FLOW_ITEM,
      payload: { nodeId: selectedFlowItemNodeId, serviceData: selectedItemData },
    });
    void loadAssociatedServicesForNode(dispatch, state.flowItemDataMap, selectedFlowItemNodeId, state.cemRootNodeId);

    const cemFlowItem = state.flowItems.find((fi) => fi.nodeId === selectedFlowItemNodeId);
    const { width: viewportWidth, height: viewportHeight } = state.flowBuilderSizeState;
    if (cemFlowItem && viewportWidth > 0 && viewportHeight > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          flowBuilderRef.current?.focusFlowItemInViewport(
            cemFlowItem.x,
            cemFlowItem.y,
            viewportWidth,
            viewportHeight,
          );
        });
      });
    }
  }, [
    dispatch,
    state.cemRootNodeId,
    state.designingCEM,
    state.flowBuilderSizeState,
    state.flowItemDataMap,
    state.flowItems,
  ]);

  // After Control Plane return, pan the viewport once step-flow items are on the canvas.
  useEffect(() => {
    const focusNodeId = pendingFlowItemFocusRef.current;
    if (!focusNodeId || !state.designingStep || state.designingCEM) {
      return;
    }
    if (state.selectedFlowItem?.nodeId !== focusNodeId) {
      return;
    }
    const flowItem = state.flowItems.find((item) => item.nodeId === focusNodeId);
    if (!flowItem) {
      return;
    }

    pendingFlowItemFocusRef.current = undefined;
    skipTranslationResetRef.current = false;

    const { width: viewportWidth, height: viewportHeight } = state.flowBuilderSizeState;
    if (viewportWidth <= 0 || viewportHeight <= 0) {
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        flowBuilderRef.current?.focusFlowItemInViewport(
          flowItem.x,
          flowItem.y,
          viewportWidth,
          viewportHeight,
        );
      });
    });
  }, [
    state.designingCEM,
    state.designingStep,
    state.flowBuilderSizeState,
    state.flowItems,
    state.selectedFlowItem?.nodeId,
  ]);

  // URL <-> state: keep the canvas's "designing a step" state in sync with the /steps/:stepName
  // segment in the URL.
  //
  // - When the URL contains a step name (refresh, deep-link, return-from-service), enter that
  //   step once journey data has loaded.
  // - When the user clicks a step card, the reducer flips state.designingStep + journeyStepName
  //   synchronously; we then push the step segment into the URL.
  // - When the user cancels / saves out of a step, we strip the step segment from the URL.
  useEffect(() => {
    if (state.viewJourneyInitiated === undefined) {
      return;
    }
    // CEM design intentionally does not participate in step-level URL sync (see plan).
    if (state.designingCEM) {
      return;
    }

    // Reducer is currently designing a step — make sure the URL reflects it.
    if (state.designingStep) {
      const desiredName = state.journeyStepName;
      if (!desiredName) {
        return;
      }
      if (desiredName !== stepNameFromUrl) {
        stepUrlResumeHandledRef.current = desiredName;
        navigate(
          buildJourneyCanvasUrl({ mode, id: journeyId, version, stepName: desiredName }),
          { replace: true }
        );
      } else {
        stepUrlResumeHandledRef.current = desiredName;
      }
      return;
    }

    // Reducer is at the journey level. The URL either has no step segment (we're done) or it
    // wants us to enter a step.
    if (!stepNameFromUrl) {
      stepUrlResumeHandledRef.current = undefined;
      return;
    }

    // The reducer just exited a step (cancel/save). Strip the now-stale step segment from the
    // URL rather than re-entering it. The ref tells us we previously handled this exact name.
    if (stepUrlResumeHandledRef.current === stepNameFromUrl) {
      stepUrlResumeHandledRef.current = undefined;
      navigate(buildJourneyCanvasUrl({ mode, id: journeyId, version }), { replace: true });
      return;
    }

    // Fresh URL with a step name (refresh / deep-link / return-from-service) — wait for step
    // data to load, then resolve the name and enter the step.
    if (state.stepItems.length === 0) {
      return;
    }

    const matchingStep = state.stepItems.find((item) => item.name === stepNameFromUrl);
    stepUrlResumeHandledRef.current = stepNameFromUrl;

    if (!matchingStep) {
      toastMessage.failure(
        translate('journey_builder_component.title_toast_error'),
        translate('journey_builder_component.msg_step_not_found')
      );
      navigate(buildJourneyCanvasUrl({ mode, id: journeyId, version }), { replace: true });
      return;
    }

    dispatch({ type: Actions.CLICK_STEP_CARD, payload: { nodeId: matchingStep.nodeId } });
  }, [
    dispatch,
    journeyId,
    mode,
    navigate,
    state.designingCEM,
    state.designingStep,
    state.journeyStepName,
    state.stepItems,
    state.viewJourneyInitiated,
    stepNameFromUrl,
    toastMessage,
    translate,
    version,
  ]);

  useEffect(() => {
    if (state.viewJourneyInitiated === undefined) {
      return;
    }
    loader.show();
    const promises: Promise<Service[] | ViewJourneyData | undefined>[] = [];
    // Get root services and journey data
    const servicesPromise = getAllServices(state.allServicesDataLoad.page, journeyCanvasConstants.allServicesPageSize, state.searchTerm);
    dispatch({
      type: Actions.LOAD_JOURNEY_METADATA,
      payload: {
        name: journeyId,
        status: isCreate ? 'DRAFT' : '',
        description: descriptionForCurrentJourney,
        version: isCreate ? 1 : 0,
        category: groupForCurrentJourney,
        type: '',
        uuid: undefined,
      }
    });
    promises.push(servicesPromise);

    fetchJourneyData({ promises, dispatch, isCloneJourney: !!cloneJourneyName && !!cloneJourneyVersion, isCreate, cloneJourneyName, cloneJourneyVersion, journeyId, versionForCurrentJourney, descriptionForCurrentJourney, groupForCurrentJourney });

    Promise.all(promises).then((data) => {
      loader.hide();
      const services = data[0] as Service[];
      if (!isCreate || (cloneJourneyName && cloneJourneyVersion)) {
        const journeyDataResp = data[1] as ViewJourneyData;
        if (journeyDataResp) {
          const { items, connectors, stepJourneyDataMap, flowItemDataMap, payloadTransformations } = prepareJourneyFromData(journeyDataResp, !!state.viewJourneyInitiated, !!cloneJourneyName && !!cloneJourneyVersion, state.designingCEM);

          journeyDataRef.current = undefined;

          dispatch({
            type: Actions.FETCH_ALL_SERVICES_AND_JOURNEY_DATA,
            payload: {
              services,
              connectors,
              stepJourneyDataMap,
              flowItemDataMap,
              items,
              payloadTransformations,
              isCreate
            }
          });
          return;
        }
        dispatch({ type: Actions.NOTIFY_API_ERROR });
        return;
      }
      dispatch({
        type: Actions.FETCH_ALL_SERVICES_AND_JOURNEY_DATA,
        payload: {
          services,
          connectors: [],
          stepJourneyDataMap: {},
          flowItemDataMap: {},
          items: [],
          payloadTransformations: [],
          isCreate
        }
      });
    });
    // Bootstrap journey + services when read-only flag is known. Do not depend on searchTerm / services page here — that would refetch the full journey on sidebar search or pagination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.viewJourneyInitiated]);

  useEffect(() => {
    // Normalizes the URL to use mode=edit after journey creation / first load. Preserves any
    // /steps/:stepName segment so a refresh or view->edit transition doesn't drop it.
    if (isCreate && state.isJourneyCreated) {
      navigate(
        buildJourneyCanvasUrl({ mode: 'edit', id: journeyId, version: '1', stepName: stepNameFromUrl }),
        { replace: true }
      );
      return;
    }
    if (
      (!isCreate && !viewJourney && state.journeyVersion)
      || (viewJourney && !state.viewJourneyInitiated && state.journeyVersion)
    ) {
      const targetVersion = state.journeyVersion.toString();
      // No-op if the URL already matches — avoids redundant navigation that could race with the
      // step-level URL sync effect.
      if (mode === 'edit' && version === targetVersion) {
        return;
      }
      navigate(
        buildJourneyCanvasUrl({ mode: 'edit', id: journeyId, version: targetVersion, stepName: stepNameFromUrl }),
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isJourneyCreated, state.viewJourneyInitiated, state.journeyVersion, isCreate, journeyId, navigate, viewJourney]);

  useEffect(() => {
    // use key up on body instead of the component to listen key event without having focus
    const bodyKeyUpHandler = (ev: KeyboardEvent) => {
      if (
        !ev.target
        || (ev.target as HTMLElement).tagName.toUpperCase() !== 'BODY'
      ) {
        return;
      }
      if (ev.key === 'Backspace' || ev.key === 'Delete') {
        // if `delete` or `backspace` is pressed remove the selected event / connector from flow
        if (state.selectedFlowItem && state.selectedFlowItem.nodeId !== state.cemRootNodeId) {
          dispatch({ type: Actions.NOTIFY_DELETE_NODE, payload: { nodeId: state.selectedFlowItem.nodeId } });
        } else if (state.selectedConnector) {
          // check convergence
          const parallelConnectors = state.flowConnectors.filter((fc) => fc.toNodeId === state.selectedConnector.to);
          if (parallelConnectors.length > 1) {
            dispatch({ type: Actions.NOTIFY_DELETE_CONNECTOR, payload: state.selectedConnector });
          }
        }
      }
    };
    document.body.addEventListener('keyup', bodyKeyUpHandler);
    return () => {
      document.body.removeEventListener('keyup', bodyKeyUpHandler);
    };
  }, [state.selectedFlowItem, state.selectedConnector, state.cemRootNodeId, state.flowConnectors]);

  // Check for enabling and disabling of save step button on header
  useEffect(() => {
    if (state.designingStep) {
      const isSubsequentStep = !!state.prevStepCardUUID;
      const eventMapDetails = createEventMapDetail(
        isSubsequentStep ? state.flowItems.slice(1) : state.flowItems,
        isSubsequentStep ? state.flowConnectors.slice(1) : state.flowConnectors,
        state.flowItemDataMap,
        state.maxExecutionTime,
        state.uniqueIdentifiers?.toString(),
        state.payloadTransformations
      );

      if (!stepDataRef.current) {
        // step journey just loaded, cache the payload
        stepDataRef.current = eventMapDetails;
        return;
      }

      if (
        eventMapDetails.eventMapNodes.length > 0
        && eventMapDetails.eventMapNodes[0].eventName !== ''
        && !_.isEqual(eventMapDetails, stepDataRef.current)) {
        dispatch({ type: Actions.SAVE_JOURNEY_BTN_ENABLE, payload: { save: true } });
      } else {
        // save button gets disabled when all services are removed
        dispatch({ type: Actions.SAVE_JOURNEY_BTN_ENABLE, payload: { save: false } });
      }

      // cem save btn gets enabled when something is changed.
      // This flag is sticky within a CEM editing session - once a change is detected,
      // it remains enabled until the CEM is saved/cancelled/deleted (reducers handle reset).
      // Sticky behaviour prevents flicker caused by:
      //   1. intermediate reducer updates that mutate node config/buttons/revision and
      //      re-apply connector rules, producing transient states that would otherwise
      //      toggle the button off.
      //   2. the initial CEM slide-animation dispatching LOAD_COMPENSATORY_EVENT_MAP,
      //      which restores saved CEM items after the root node animation completes.
      // The transient phase between DESIGN_COMPENSATORY_EVENT_MAP and the first
      // LOAD_COMPENSATORY_EVENT_MAP is skipped explicitly (flowItems has only the root
      // while initialCemData still has the saved items) so we don't lock the flag true
      // before the user has done anything.
      const initialFlowItemCount = state.initialCemData?.flowItems?.length ?? 0;
      const isTransientCemLoadPhase = state.designingCEM
        && initialFlowItemCount > 0
        && state.flowItems.length === 1;

      if (!state.cemSaveBtn && !isTransientCemLoadPhase) {
        const initialFlowItems = state.initialCemData?.flowItems ?? [];
        const initialFlowConnectors = state.initialCemData?.flowConnectors ?? [];
        const flowItemsUnchanged = _.isEqual(initialFlowItems, state.flowItems.slice(1));
        const flowConnectorsUnchanged = _.isEqual(initialFlowConnectors, state.flowConnectors);

        // Check if condExpr data has changed in flowItemDataMap
        const hasCondExprChanges = Object.keys(state.flowItemDataMap).some((nodeId) => {
          const currentCondExpr = state.flowItemDataMap[nodeId]?.condExpr;
          const initialCondExpr = state.initialCemCondExprData?.[nodeId]?.condExpr;

          // If both are empty or undefined, no change
          if ((!currentCondExpr || Object.keys(currentCondExpr).length === 0)
            && (!initialCondExpr || Object.keys(initialCondExpr).length === 0)) {
            return false;
          }

          return !_.isEqual(currentCondExpr, initialCondExpr);
        });

        if (!(flowItemsUnchanged && flowConnectorsUnchanged && !hasCondExprChanges)) {
          dispatch({ type: Actions.CEM_SAVE_BTN_ENABLE, payload: { saveCem: true } });
        }
      }
    }
  }, [
    state.flowItems,
    state.flowConnectors,
    state.payloadTransformations,
    state.flowItemDataMap,
    state.designingStep,
    state.designingCEM,
    state.initialCemCondExprData,
    state.initialCemData,
    state.maxExecutionTime,
    state.prevStepCardUUID,
    state.uniqueIdentifiers,
    state.cemSaveBtn,
  ]);

  useEffect(() => {
    if (!state.designingStep) {
      const { stepNodes } = createJourneyStepNodes({ stepItems: state.stepItems, stepData: state.stepJourneyDataMap, stepEdges: state.stepConnectors });
      const { stepEdges } = createJourneyStepEdges(state.stepConnectors);
      const stepDetails = createJourneyStepDetails({ cloneJourneyName, stepItems: state.stepItems, stepData: state.stepJourneyDataMap, itemDataMap: state.flowItemDataMap, stepEdges: state.stepConnectors, payloadTransformation: state.payloadTransformations });
      const data = { nodes: stepNodes, edges: stepEdges, stepDetails };

      if (!journeyDataRef.current) {
        // journey just loaded, cache the payload
        journeyDataRef.current = data;
      }

      if (state.stepItems.length === 0) {
        dispatch({ type: Actions.SAVE_JOURNEY_BTN_ENABLE, payload: { save: false } });
        return;
      }

      if (!_.isEqual(data, journeyDataRef.current)) {
        dispatch({ type: Actions.SAVE_JOURNEY_BTN_ENABLE, payload: { save: true } });
      } else {
        // save button gets disabled when all services are removed
        dispatch({ type: Actions.SAVE_JOURNEY_BTN_ENABLE, payload: { save: false } });
      }
    }
  }, [state.stepItems, state.stepConnectors, state.stepJourneyDataMap, state.designingStep, state.payloadTransformations, cloneJourneyName, state.flowItemDataMap]);

  /**
   * @method goToPrevPage
   * @description Navigates to the previous page
  */
  const goToPrevPage = (isUpdated?: boolean) => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    // The location.key property is a unique string associated with this location. On the initial location, this will be the string default. On all subsequent locations, this string will be a unique identifier.
    if (location.key === 'default' || isUpdated) {
      navigate(journeyBuilderRoutes.viewJourney);
    } else {
      navigate(-1);
    }
  };

  /**
  * @method onCancelHandler
   * @description Header cancel button handler
  */
  const onCancelHandler = (leave: boolean) => {
    // close Event Schema sidebar on any breadcrumb/cancel click
    if (state.selectedEventSchema) {
      dispatch({ type: Actions.CLEAR_EVENT_SCHEMA });
      dispatch({ type: Actions.HIDE_COND_EXP_MODAL });
    }
    if (leave) {
      if (state.saveJourneyBtnEnabled && !state.viewJourneyInitiated) {
        (async () => {
          const result = await confirmDialog.show({
            message: translate('journey_builder_component.msg_leave_confirm'),
            title: translate('journey_builder_component.title_leave_confirm'),
            okText: translate('journey_builder_component.btn_confirm_leave_confirm'),
            cancelText: translate('journey_builder_component.btn_close_confirm_confirm'),
            testId: 'leaveConfirmModal'
          });
          if (result) {
            navigate(journeyBuilderRoutes.viewJourney);
          }
        })();
        return;
      }
      navigate(journeyBuilderRoutes.viewJourney);
      return;
    }
    if (state.saveJourneyBtnEnabled && !state.viewJourneyInitiated) {
      setShowCancelWarningModal(true);
      return;
    }
    if (state.designingStep) {
      stepDataRef.current = undefined;
      dispatch({ type: Actions.CANCEL_STEP_DESIGN });
      return;
    }
    goToPrevPage();
  };

  /**
   * @method serviceClickHandler
   * @description clicking the event form the list to show in canvas
   * @param service Check if the service belongs to all Services or Associated service
   * @param type Checks dragging item type
   */
  const serviceClickHandler = (
    service: Service | AssociatedService,
    type: DraggingItemType,
    serviceOperation?: ServiceOperation,
    ingressEvent?: IngressEvent,
  ) => {
    // don't do anything if viewing journey
    if (state.viewJourneyInitiated) {
      return;
    }
    const isCompensateAction = computeIsCompensateAction({
      designingCEM: state.designingCEM,
      serviceType: service.serviceType,
      svcIsCompensateAction: service?.isCompensateAction,
      ingressEvent,
    });
    dispatch({ type: Actions.PICK_SERVICE, payload: { service, serviceOperation, ingressEvent, type, isCompensateAction: isCompensateAction ?? false } });
  };

  /**
   * @method loadMoreServices
   * @description loading more services when scroll down on service list
   */
  const loadMoreServices = () => {
    (async () => {
      const services = await getAllServices(state.allServicesDataLoad.page + 1, journeyCanvasConstants.allServicesPageSize, state.searchTerm);
      if (services) {
        dispatch({ type: Actions.MORE_SERVICES_LOADED, payload: { services } });
      } else {
        dispatch({ type: Actions.MORE_SERVICES_LOAD_FAILED });
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_services_load_more_fail'));
      }
    })();
    dispatch({ type: Actions.LOAD_MORE_SERVICES });
  };

  /**
   * @method sidebarScrollEventHandler
   * @description scrolling sidebar to load more services
   * @param {React.UIEvent<HTMLDivElement>} ev
   */
  const sidebarScrollEventHandler = (ev: React.UIEvent<HTMLDivElement>) => {
    const { height } = ev.currentTarget.getBoundingClientRect();
    const { scrollTop, scrollHeight } = ev.currentTarget;

    if (scrollTop + height + 600 > scrollHeight) {
      if (!state.allServicesDataLoad.loadingMore && !state.allServicesDataLoad.noLoadMore && state.sidebarState === SidebarState.ALL_SERVICES) {
        loadMoreServices();
      }
    }
  };

  /**
   * @method createSaveJourneyHandler
   * @description Creates a handler for saving the journey
   */
  const createSaveJourneyHandler = async () => {
    const isNotPayloadData = state.flowItems.find((item) => ((item.conditionText === 'AND' || item.conditionText === 'T-AND') && !item.isPayloadTransformationDataSelected));
    if (isNotPayloadData && !(await confirmDialog.show({
      message: translate('journey_builder_component.msg_add_payload'),
      title: translate('journey_builder_component.title_payload_not_found'),
      okText: translate('journey_builder_header_component.btn_yes'),
      cancelText: translate('journey_builder_header_component.btn_no'),
      testId: 'payloadNotSelectedModal'
    }))) {
      return;
    }
    if (!state.isJourneyCreated) {
      createJourneyHandler({ navigate, cloneJourneyName, setEnableSetForApprovalBtn, stepDataRef, journeyDataRef, loader, toastMessage, translate, dispatch, designingStep: state.designingStep, stepItems: state.stepItems, deletedStepItems: state.deletedStepItems, stepConnectors: state.stepConnectors, deletedStepConnectors: state.deletedStepConnectors, stepJourneyDataMap: state.stepJourneyDataMap, flowItemDataMap: state.flowItemDataMap, payloadTransformations: state.payloadTransformations, journeyName: state.journeyName, journeyDescription: state.journeyDescription, journeyType: state.journeyType, journeyCategory: state.journeyCategory });
    } else {
      updateJourneyHandler({ navigate, cloneJourneyName, setEnableSetForApprovalBtn, stepDataRef, journeyDataRef, loader, toastMessage, translate, dispatch, designingStep: state.designingStep, journeyVersion: state.journeyVersion, stepItems: state.stepItems, deletedStepItems: state.deletedStepItems, stepConnectors: state.stepConnectors, deletedStepConnectors: state.deletedStepConnectors, stepJourneyDataMap: state.stepJourneyDataMap, flowItemDataMap: state.flowItemDataMap, payloadTransformations: state.payloadTransformations, journeyName: state.journeyName, journeyDescription: state.journeyDescription, journeyType: state.journeyType, journeyCategory: state.journeyCategory });
    }
  };

  // load the service operations when ingress event node if changes
  useEffect(() => {
    if (!state.ingressNodeId) {
      return;
    }
    const flowItemData = state.flowItemDataMap[state.ingressNodeId];
    if (!flowItemData) {
      return;
    }
    dispatch({ type: Actions.SIDEBAR_DATA_LOADING });
    let serviceOperations: ServiceOperation[] | undefined;
    const service = state.allServices.find((services) => services.serviceName === flowItemData.serviceName && services.version === flowItemData.serviceVersion);
    (async () => {
      if (!service) {
        const getService = await getServiceByVersionAndName(flowItemData.serviceVersion, flowItemData.serviceName);
        serviceOperations = getService ? getService[0].serviceOperations : undefined;
      } else {
        serviceOperations = service.serviceOperations;
      }
      if (serviceOperations) {
        dispatch({ type: Actions.FETCH_SERVICE_OPERATIONS, payload: { serviceOperations } });
      } else {
        dispatch({ type: Actions.SIDEBAR_DATA_ERROR });
      }
    })();
  }, [state.ingressNodeId, state.allServices, state.flowItemDataMap]);

  useEffect(() => {
    // create slide animation
    if (state.cemRootNodeId) {
      if (animatedCemRootNodeRef.current === state.cemRootNodeId) {
        return;
      }
      animatedCemRootNodeRef.current = state.cemRootNodeId;
      restoredParentJourneyNodeRef.current = undefined;
      const nodeId = state.cemRootNodeId;
      flowBuilderRef.current?.slideElement(
        nodeId,
        {
          x: journeyCanvasConstants.rootExternalItemPosition.x,
          y: journeyCanvasConstants.rootExternalItemPosition.y
        }
      ).finally(() => {
        // update the state with new position
        dispatch({
          type: Actions.LOAD_COMPENSATORY_EVENT_MAP,
        });
      });
    } else if (state.flowItems.length === 1) {
      // create slide animation with the fist flow item
      const { nodeId } = state.flowItems[0];
      if (restoredParentJourneyNodeRef.current === nodeId) {
        return;
      }
      const parentJourneyElem = state.parentJourney?.flowItems?.find((i) => i.nodeId === nodeId);
      if (parentJourneyElem) {
        restoredParentJourneyNodeRef.current = nodeId;
        animatedCemRootNodeRef.current = undefined;
        flowBuilderRef.current?.slideElement(
          nodeId,
          {
            x: parentJourneyElem.x,
            y: parentJourneyElem.y
          }
        ).finally(() => {
          // update the state with new position
          dispatch({
            type: Actions.RESTORE_PARENT_JOURNEY,
          });
        });
      }
    }
  }, [state.cemRootNodeId, state.flowItems, state.parentJourney]);

  /**
   * @method saveCompensatoryEventMapHandler
   * @description error handling for save compensatory event map
   */
  const saveCompensatoryEventMapHandler = async () => {
    let versionToUse = state.journeyVersion;

    if (isStatusActiveOrInactive) {
      const allStepItems = [...state.stepItems, ...state.deletedStepItems];
      const allStepConnectors = [...state.stepConnectors, ...state.deletedStepConnectors];
      const { stepNodes } = createJourneyStepNodes({ stepItems: allStepItems, stepData: state.stepJourneyDataMap, stepEdges: allStepConnectors });
      const { stepEdges } = createJourneyStepEdges(state.stepConnectors);
      const stepDetails = createJourneyStepDetails({ cloneJourneyName, stepItems: allStepItems, stepData: state.stepJourneyDataMap, itemDataMap: state.flowItemDataMap, stepEdges: allStepConnectors, payloadTransformation: state.payloadTransformations });

      const completeJourney: CreateJourneyData = {
        journeyName: state.journeyName,
        journeyDescription: state.journeyDescription,
        journeyType: state.journeyType,
        journeyCategory: state.journeyCategory,
        journeyJsonDetails: { stepNodes, stepEdges },
        stepDetails,
      };

      loader.show();
      const resp = await saveJourney(completeJourney, state.journeyVersion);
      loader.hide();

      if (!resp) {
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_journey_save_failed'));
        return;
      }

      journeyDataRef.current = undefined;
      dispatch({
        type: Actions.SAVE_JOURNEY_SUCCESS,
        payload: {
          uuid: resp.uuid,
          steps: resp.stepDetails,
          status: resp.status,
          version: resp.version,
          comments: resp.journeyStatusHistory,
        }
      });

      versionToUse = resp.version;
    }

    saveCompensatoryEventMap({ loader, toastMessage, translate, dispatch, flowItems: state.flowItems, flowConnectors: state.flowConnectors, compensatoryEventMap: state.compensatoryEventMap, flowItemDataMap: state.flowItemDataMap, cemRootNodeId: state.cemRootNodeId, journeyName: state.journeyName, journeyStepName: state.journeyStepName, version: versionToUse, maxExecutionTime: state.maxExecutionTime, uniqueIdentifiers: state.uniqueIdentifiers, payloadTransformations: state.payloadTransformations, readOnly: !!state.viewJourneyInitiated });
  };

  // Reset pan/zoom when leaving step or CEM design — unless Control Plane return is about
  // to position the viewport on a previously selected flow item inside a step.
  // At the journey-card overview, align cards to the fixed anchor (same idea as zoom controls).
  useEffect(() => {
    if (skipTranslationResetRef.current) {
      skipTranslationResetRef.current = false;
      return;
    }
    if (!state.designingStep && !state.designingCEM) {
      resetJourneyCardsViewport();
    } else {
      requestAnimationFrame(() => {
        flowBuilderRef.current?.resetViewport();
      });
    }
  }, [state.designingStep, state.designingCEM]);

  // Align journey cards when step data first loads or changes on the overview screen.
  useEffect(() => {
    if (state.designingStep || state.designingCEM) {
      return;
    }
    if (state.stepItems.length === 0 && !state.initiateStepCardVisible) {
      return;
    }
    resetJourneyCardsViewport();
  }, [state.stepItems.length, state.initiateStepCardVisible, state.designingStep, state.designingCEM]);

  const commentBtnHandler = () => {
    dispatch({
      type: Actions.SET_SIDEBAR_FOR_COMMENT_HISTORY,
    });
  };

  // get eventName for scheduler
  const getSchedulerEventName = () => {
    let eventName = '';
    if (state.stepItems && state.stepItems.length > 0) {
      const schedulerEventNodeId = state.stepJourneyDataMap[state.stepItems[0].nodeId].ingressNodeId;
      eventName = state.flowItemDataMap[schedulerEventNodeId]?.ingressEvent ?? '';
    }
    return eventName;
  };

  // get eventVersion for scheduler
  const getSchedulerEventVersion = () => {
    let eventVersion = 1;
    if (state.stepItems && state.stepItems.length > 0) {
      const schedulerEventNodeId = state.stepJourneyDataMap[state.stepItems[0].nodeId].ingressNodeId;
      eventVersion = state.flowItemDataMap[schedulerEventNodeId]?.ingressEventVersion ?? 1;
    }
    return eventVersion;
  };

  const getSchedularStepName = () => {
    if (state.stepItems.length > 0) {
      return state.stepJourneyDataMap[state.stepItems[0].nodeId]?.journeyStepName ?? '';
    }
    return '';
  };

  const dismissExternalOnSubsequentStepModalHandler = () => {
    dispatch({ type: Actions.HIDE_EXTERNAL_ON_SUBSEQUENT_STEP });
  };

  /**
   * @method handlePayloadInfoModalDismiss
   * @description Handles dismissal of payload info modal - dismisses popup and proceeds with condition change if pending
   */
  const handlePayloadInfoModalDismiss = () => {
    setShowPayloadInfoModal(false);
    if (pendingConditionChange) {
      conditionSelectHandler(pendingConditionChange.nodeId, dispatch, state.flowItems, pendingConditionChange.conditionText);
      setPendingConditionChange(null);
    }
  };

  const clearOrToAndPending = () => {
    setPendingOrToAndMergeId(null);
    setPendingCondExpIngressLabels([]);
  };

  const applyOrToAndToggle = () => {
    if (pendingOrToAndMergeId) {
      conditionSelectHandler(pendingOrToAndMergeId, dispatch, state.flowItems, FlowItemConditions.AND);
    }
    clearOrToAndPending();
  };

  /**
   * @method handleOrToAndPayloadModalAccept
   * @description Continues past the payload warning; may open the conditional-expression modal next.
   */
  const handleOrToAndPayloadModalAccept = () => {
    setShowOrToAndPayloadModal(false);
    if (pendingCondExpIngressLabels.length >= 1) {
      setShowOrToAndCondExpModal(true);
    } else {
      applyOrToAndToggle();
    }
  };

  /**
   * @method handleOrToAndPayloadModalCancel
   * @description Dismisses the payload warning without toggling the merge.
   */
  const handleOrToAndPayloadModalCancel = () => {
    setShowOrToAndPayloadModal(false);
    clearOrToAndPending();
  };

  /**
   * @method handleOrToAndCondExpModalAccept
   * @description Confirms clearing conditional expressions and applies OR→AND.
   */
  const handleOrToAndCondExpModalAccept = () => {
    setShowOrToAndCondExpModal(false);
    applyOrToAndToggle();
  };

  /**
   * @method handleOrToAndCondExpModalCancel
   * @description Dismisses the conditional-expression warning without toggling.
   */
  const handleOrToAndCondExpModalCancel = () => {
    setShowOrToAndCondExpModal(false);
    clearOrToAndPending();
  };

  /**
   * @method handleConditionClick
   * @description Opens popover to select condition type (OR, AND, T-AND)
   */
  const handleConditionClick = (nodeId: string, clientX: number, clientY: number) => {
    if (state.viewJourneyInitiated) {
      return;
    }
    const node = state.flowItems.find((fi) => fi.nodeId === nodeId);
    if (!node) {
      return;
    }
    setConditionPopover({ nodeId, x: clientX, y: clientY, current: node.conditionText ?? FlowItemConditions.OR });
  };

  const handleConditionSelect = (selectedCondition: string) => {
    if (!conditionPopover) {
      return;
    }
    const { nodeId, current } = conditionPopover;
    setConditionPopover(null);

    console.log('[ConditionSelect] nodeId:', nodeId, '| previous:', current, '| selected:', selectedCondition);

    if (selectedCondition === current) {
      return;
    }

    const node = state.flowItems.find((fi) => fi.nodeId === nodeId);
    if (!node) {
      return;
    }

    const requiresPayload = selectedCondition === FlowItemConditions.AND || selectedCondition === FlowItemConditions.T_AND;

    if (requiresPayload) {
      const updatedFlowItems = state.flowItems.map((item) => {
        if (item.nodeId === nodeId) {
          return { ...item, conditionText: selectedCondition };
        }
        return item;
      });

      const isOrToAnd = current === FlowItemConditions.OR && selectedCondition === FlowItemConditions.AND;
      if (isOrToAnd) {
        const needsPayloadWarning = !node.isPayloadTransformationDataSelected;
        const condExpIngressLabels = listClearableCondExprIngressLabelsForMerge(
          nodeId,
          state.flowConnectors,
          toFlowMergeRefs(updatedFlowItems),
          state.flowItemDataMap
        );
        if (needsPayloadWarning || condExpIngressLabels.length >= 1) {
          setPendingOrToAndMergeId(nodeId);
          setPendingCondExpIngressLabels(condExpIngressLabels);
          if (needsPayloadWarning) {
            setShowOrToAndPayloadModal(true);
          } else {
            setShowOrToAndCondExpModal(true);
          }
          return;
        }
      }

      const isNotPayloadData = updatedFlowItems.find((item) => (
        (item.conditionText === FlowItemConditions.AND || item.conditionText === FlowItemConditions.T_AND) && !item.isPayloadTransformationDataSelected
      ));

      if (isNotPayloadData) {
        setPendingConditionChange({ nodeId, conditionText: selectedCondition });
        setShowPayloadInfoModal(true);
        return;
      }
    }

    conditionSelectHandler(nodeId, dispatch, state.flowItems, selectedCondition);
  };

  useEffect(() => {
    if (state.designingCEM) {
      setSummaryStats({ eventCount: state.flowConnectors.length, serviceCount: (state.flowItems.length - 1), cemCount: 0 });
    } else {
      let items = [...state.stepItems];
      if (state.designingStep) {
        items = state.stepItems.filter((e) => e.nodeId === state.stepCardUUID);
      }
      const sumWithInitial = items.reduce((accumulator, currentValue) => {
        accumulator.serviceCount += currentValue.serviceCount;
        accumulator.eventCount += currentValue.eventCount;
        accumulator.cemCount += currentValue.cemCount;
        return accumulator;
      }, { serviceCount: 0, eventCount: 0, cemCount: 0 });
      setSummaryStats(sumWithInitial);
    }
  }, [state.stepItems, state.stepCardUUID, state.designingStep, state.designingCEM, state.flowConnectors, state.flowItems]);

  const onDeleteSuccess = () => {
    getJourney(journeyId, versionForCurrentJourney).then((resp) => {
      if (resp) {
        dispatch({
          type: Actions.LOAD_JOURNEY_METADATA,
          payload: {
            name: resp.journeyName,
            status: resp.status || 'DRAFT',
            description: resp.journeyDescription,
            version: resp.version || 1,
            type: resp.journeyType,
            category: resp.journeyCategory,
            uuid: resp.uuid,
            comments: resp.journeyStatusHistory
          }
        });
      }
      return resp;
    });
  };

  return (
    <>
      {toastMessage.renderToast()}
      {loader.render()}
      {confirmDialog.render()}
      <ConfirmDeleteModal
        notifyDialogStatus={state.notifyDialogStatus}
        designingCEM={state.designingCEM}
        showNotifyDialog={state.showNotifyDialog}
        confirmDelete={() => confirmDelete({ loader, toastMessage, translate, dispatch, notifyDialogStatus: state.notifyDialogStatus, flowItemDataMap: state.flowItemDataMap, cemRootNodeId: state.cemRootNodeId, journeyName: state.journeyName, journeyStepName: state.journeyStepName, journeyVersion: state.journeyVersion, deletingNodeId: state.deletingNodeId, designingCEM: state.designingCEM })}
        hideDeleteNotifyDialogHandler={() => hideDeleteNotifyDialogHandler(dispatch)}
      />
      <div className={document.fullscreenElement ? classes.fullscreen : ''}>
        <JourneyBuilderHeader
          journeyStepName={state.designingStep ? state.journeyStepName : ''}
          enableSaveBtn={state.saveJourneyBtnEnabled}
          disableAllBtn={state.designingCEM}
          enableSendForApproveBtn={enableSetForApprovalBtn && (!state.designingStep && state.stepItems.length > 0) && !(['active', 'inactive', 'returned'].includes(state.journeyStatus.toLowerCase()))}
          onSave={createSaveJourneyHandler}
          onCancel={onCancelHandler}
          saveBtnText={!state.designingStep ? translate('journey_builder_component.btn_save_cem') : translate('journey_builder_component.txt_save_step')}
          showSchedulerBtn={!state.designingStep}
          onSchedulerClick={() => setOpenSchedulerModal(dispatch)}
          journeyName={state.journeyName}
          journeyStatus={state.journeyStatus}
          journeyVersion={state.journeyVersion}
          onEdit={() => onEditHandler({ setShowModalForActiveEdit, loader, toastMessage, translate, dispatch, journeyStatus: state.journeyStatus, journeyName: state.journeyName })}
          onApprove={() => setShowApproveWarningModal(true)}
          onReturn={() => setShowReturnModal(true)}
          onSendForApproval={() => setShowSendForApprovalWarningModal(true)}
          viewJourneyInitiated={state.viewJourneyInitiated}
          onFullscreen={fullScreenHandler}
          commentBtnHandler={commentBtnHandler}
          onDeleteSuccess={onDeleteSuccess}
          onMoveToDraft={() => setShowMoveToDraftWarningModal(true)}
          journeyDescription={(() => {
            if (!state.journeyComments || state.journeyComments.length === 0) {
              return String(translate('journey_builder_component.msg_no_description_provided'));
            }
            const pendingApprovalEntry = state.journeyComments.find(
              (entry) => entry.status?.toUpperCase() === 'PENDING_APPROVAL'
            );
            if (!pendingApprovalEntry) {
              return String(translate('journey_builder_component.msg_no_description_provided'));
            }
            if (pendingApprovalEntry.description && pendingApprovalEntry.description.trim()) {
              return pendingApprovalEntry.description.trim();
            }
            return String(translate('journey_builder_component.msg_no_description_provided'));
          })()}
        />
        <div className={`${classes.container}`} data-testid="journeyCanvas">
          <div className={`${classes['journey-builder']} ${state.designingCEM ? classes['designing-cem'] : ''}`}>
            <div
              className={classes['canvas-root']}
              ref={canvasRootRef}
              onDrop={(ev) => dropEndHandler({ ev, canvasRoot: canvasRootRef.current, flowBuilderRef: flowBuilderRef.current, dispatch, draggingItem: state.draggingItem, designingCEM: state.designingCEM, readOnly: !!state.viewJourneyInitiated })}
            >
              <ErrorBoundary
                FallbackComponent={ErrorFallBack}
                onReset={
                  () => {
                    goToPrevPage();
                  }
                }
              >
                <FlowBuilder
                  className={classes.canvas}
                  height={
                    state.flowBuilderSizeState.height * window.devicePixelRatio
                  }
                  width={state.flowBuilderSizeState.width * window.devicePixelRatio}
                  items={state.designingStep ? state.flowItems : []}
                  stepItems={state.designingStep ? [] : state.stepItems}
                  connectors={state.designingStep ? state.flowConnectors : state.stepConnectors}
                  onElementSelect={(nodeId) => elementSelectHandler({ dispatch, flowItemDataMap: state.flowItemDataMap, designingStep: state.designingStep, cemRootNodeId: state.cemRootNodeId, nodeId })}
                  onConnectorDraw={(from, to, orientation) => connectorDrawHandler({ from, to, orientation, loader, dispatch, flowItemDataMap: state.flowItemDataMap })}
                  onConnectorSelect={(connector) => connectorSelectHandler(dispatch, connector)}
                  onDragComplete={(nodeId, x, y) => flowItemDragCompleteHandler(nodeId, x, y, dispatch)}
                  config={FLOW_CONFIG}
                  ref={flowBuilderRef}
                  selectedItemNodeId={state.selectedFlowItem?.nodeId}
                  selectedConnector={state.selectedConnector}
                  style={{
                    width: state.flowBuilderSizeState.width,
                    height: state.flowBuilderSizeState.height
                  }}
                  onStartBtnClick={() => startBtnClickHandler(dispatch)}
                  startBtnSelected={state.startSelected}
                  startBtnVisible={!state.designingCEM && !state.isRootExternal && state.designingStep && state.designingInitialStepJourney}
                  onItemBtnClick={(id, nodeId) => itemButtonClickHandler({ id, nodeId, loader, toastMessage, translate, dispatch, flowItemDataMap: state.flowItemDataMap, journeyVersion: state.journeyVersion, readOnly: !!state.viewJourneyInitiated })}
                  onConnectorBtnClick={(btnId, connector) => connectorBtnClickHandler(
                    btnId,
                    connector,
                    dispatch,
                    state.eventSchemaMap,
                    state.flowItemDataMap,
                    toastMessage,
                    translate,
                    (state.designingStep || state.designingCEM) ? state.flowItems : [],
                    (state.designingStep || state.designingCEM) ? state.flowConnectors : state.stepConnectors
                  )}
                  onConditionClick={(nodeId, clientX, clientY) => handleConditionClick(nodeId, clientX, clientY)}
                  initiateStepCardVisible={!state.designingStep && state.initiateStepCardVisible}
                  onInitiateStepCardClick={() => initiateStepClickHandler(dispatch)}
                  onEgressClick={(id, orientation, type) => egressClickHandler({ id, type, dispatch, designingStep: state.designingStep })}
                  onStepCardMenuClick={(id, mouseX, mouseY) => stepCardMenuClickHandler(id, mouseX, mouseY, dispatch)}
                  readOnly={state.viewJourneyInitiated}
                />
              </ErrorBoundary>
              {state.stepCardMenuDropdown.isVisible && (
                <>
                  <ButtonGroup data-testid="dismissStepCardMenuBackDrop" className={classes['backdrop-div']} onClick={() => dismissStepCardMenuDropdown(dispatch)} />
                  <BDropdown.Menu
                    show
                    style={{
                      position: 'fixed',
                      left: state.stepCardMenuDropdown.xCoordinate,
                      top: state.stepCardMenuDropdown.yCoordinate
                    }}
                  >
                    <BDropdown.Item disabled={state.viewJourneyInitiated} onClick={() => deleteStepCardHandler(dispatch)}>{translate('journey_builder_component.btn_delete')}</BDropdown.Item>
                    <BDropdown.Item onClick={() => editStepDetailsHandler(dispatch)} data-testid="editStepDetails">{translate(state.viewJourneyInitiated ? 'journey_builder_component.btn_view_step_details' : 'journey_builder_component.btn_edit_step_details')}</BDropdown.Item>
                  </BDropdown.Menu>
                </>
              )}
              {conditionPopover && (
                <>
                  <div
                    role="presentation"
                    className={classes['condition-popover-backdrop']}
                    onClick={() => setConditionPopover(null)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setConditionPopover(null); }}
                    data-testid="dismissConditionPopover"
                  />
                  <div
                    role="menu"
                    className={classes['condition-popover']}
                    style={{ left: conditionPopover.x, top: conditionPopover.y }}
                  >
                    {[FlowItemConditions.OR, FlowItemConditions.AND, FlowItemConditions.T_AND].map((condition) => (
                      <div
                        role="menuitem"
                        tabIndex={0}
                        key={condition}
                        className={`${classes['condition-option']}${conditionPopover.current === condition ? ` ${classes.active}` : ''}`}
                        onClick={() => handleConditionSelect(condition)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleConditionSelect(condition); }}
                        data-testid={`condition-option-${condition}`}
                      >
                        {condition}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            {state.designingCEM && (
              <CemHeader
                cemRootNodeName={state.cemRootNodeName}
                viewJourneyInitiated={state.viewJourneyInitiated}
                cemName={state.flowItemDataMap[state.cemRootNodeId ?? '']?.compensatoryEventMapName}
                isStatusActiveOrInactive={isStatusActiveOrInactive}
                deleteCompensatoryEventMapHandler={() => deleteCompensatoryEventMapHandler(dispatch, state.flowItemDataMap, state.cemRootNodeId)}
                flowItemLength={state.flowItems.length}
                cancelCEMHandler={() => cancelCEMHandler(dispatch)}
                saveCompensatoryEventMapHandler={saveCompensatoryEventMapHandler}
                ingressEvent={state.flowItemDataMap[state.ingressNodeId]?.ingressEvent}
                cemSaveBtn={state.cemSaveBtn}
              />
            )}
            {state.selectedEventSchema && !state.showCondExpModal && (
              <div className={`${classes.sidebar} ${classes['sidebar--wide']} ${classes['sidebar--left']}`} data-testid="sidebar" onScroll={sidebarScrollEventHandler}>
                <Row>
                  <EventSchemaSidebar onClose={() => dispatch({ type: Actions.CLEAR_EVENT_SCHEMA })} eventSchema={state.selectedEventSchema} />
                </Row>
              </div>
            )}
            {state.showCondExpModal && (
              <div className={`${classes.sidebar} ${classes['sidebar--wide']} ${classes['sidebar--left']}`} data-testid="sidebar" onScroll={sidebarScrollEventHandler}>
                <Row>
                  <ExpressionBuilderSidebar
                    onClose={onCloseExpressionBuilderSidebar}
                    eventSchema={state.selectedEventSchema}
                    expressionData={state.condExpData}
                    onExpressionsUpdated={(e) => dispatch({
                      type: Actions.COND_EXP_SAVE,
                      payload: { expr: e.expressionStr, exprBuilderStr: e.expressionBuilderStr, toNodeId: e.toNodeId, fromNodeId: e.fromNodeId }
                    })}
                    readOnly={state.viewJourneyInitiated}
                  />
                </Row>
              </div>
            )}
            {((state.sidebarState && (state.designingStep || state.designingCEM)) || state.sidebarState === SidebarState.RETURNED_COMMENT_HISTORY) && (
              <div className={classes.sidebar} data-testid="sidebar" onScroll={sidebarScrollEventHandler}>
                <Row>
                  {state.sidebarState === SidebarState.ALL_SERVICES && (
                    <AllServices hideSidebar={() => hideSidebar(dispatch)} serviceFilterHandler={(e) => serviceFilterHandler(e, dispatch, state.sidebarState)} filteredServices={state.filteredServices} allServices={state.allServices} serviceClickHandler={serviceClickHandler} dragStartHandler={(item, itemType, ev) => dragStartHandler(item, itemType, ev, dispatch)} allServicesDataLoad={state.allServicesDataLoad} loadMoreServices={loadMoreServices} searchTerm={state.searchTerm} sidebarDataLoadingState={state.sidebarDataLoadingState} invalidSearch={state.validation.sidebarInvalidSearch} journeyName={state.journeyName} journeyVersion={state.journeyVersion} journeyMode={mode} stepCardUUID={state.designingStep ? state.stepCardUUID : undefined} stepName={state.designingStep ? state.journeyStepName : undefined} selectedFlowItemNodeId={state.designingStep ? state.selectedFlowItem?.nodeId : undefined} cemRootNodeId={state.designingCEM ? state.cemRootNodeId : undefined} designingCEM={state.designingCEM} />
                  )}
                  {state.sidebarState === SidebarState.INGRESS_EVENTS && (
                    <IngressEvents hideSidebar={() => hideSidebar(dispatch)} flowItemDataMap={state.flowItemDataMap} ingressNodeId={state.ingressNodeId} serviceOperations={state.serviceOperations} ingressEventClickHandler={(serviceOps, ingressEvent) => ingressEventClickHandler(serviceOps, ingressEvent, dispatch)} sidebarDataLoadingState={state.sidebarDataLoadingState} readOnly={state.viewJourneyInitiated} />
                  )}
                  {state.sidebarState === SidebarState.ASSOCIATED_SERVICES && (
                    <AssociatedServices
                      hideSidebar={() => hideSidebar(dispatch)}
                      associatedServices={state.associatedServices}
                      serviceClickHandler={serviceClickHandler}
                      dragStartHandler={(item, itemType, serviceOperation, ingressEvent, ev) => dragStartHandler(item, itemType, ev, dispatch, serviceOperation, ingressEvent)}
                      sidebarDataLoadingState={state.sidebarDataLoadingState}
                      isInitialCemService={state.designingCEM && state.flowItems.length === 1}
                      journeyName={state.journeyName}
                      journeyVersion={state.journeyVersion}
                      journeyMode={mode}
                      stepCardUUID={state.designingStep ? state.stepCardUUID : undefined}
                      stepName={state.designingStep ? state.journeyStepName : undefined}
                      selectedFlowItemNodeId={state.designingStep ? state.selectedFlowItem?.nodeId : undefined}
                      cemRootNodeId={state.designingCEM ? state.cemRootNodeId : undefined}
                      designingCEM={state.designingCEM}
                    />
                  )}
                  {state.sidebarState === SidebarState.RETURNED_COMMENT_HISTORY && (
                    <CommentHistoryModal hideSidebar={() => hideSidebar(dispatch)} listOfComments={state.journeyComments} sidebarDataLoadingState={state.sidebarDataLoadingState} />
                  )}
                </Row>
                {
                  state.sidebarDataLoadingState === SidebarDataLoadingState.LOADING && (
                    <div className="text-center mt-5">
                      <img src={SidebarLoader} alt="Not Found" />
                    </div>
                  )
                }
                {
                  state.sidebarDataLoadingState === SidebarDataLoadingState.ERROR && (
                    <div className="text-center mt-5">
                      <h3><b>{translate('journey_builder_component.txt_sidebar_data_load_fail')}</b></h3>
                    </div>
                  )
                }
              </div>
            )}
          </div>
        </div>
        <JourneyBuilderFooter summaryStats={summaryStats} isCem={state.designingCEM} />
      </div>
      <JourneyStepDetailsModal
        showJourneyStepModal={state.showJourneyStepModal}
        saveStepModalHandler={(journeyStepName, maxExecutionTimeInput, uniqueIdentifiers, journeyStepDescription, endpointNodeId, isEdit) => saveStepModalHandler({ journeyStepName, maxExecutionTimeInput, uniqueIdentifiers, journeyStepDescription, toastMessage, translate, dispatch, stepJourneyDataMap: state.stepJourneyDataMap, endpointNodeId, isEdit })}
        closeStepModalHandler={() => closeStepModalHandler(dispatch)}
        dropdownEndpointValues={state.dropdownEndpointValues}
        values={{
          journeyStepName: state.journeyStepName,
          maxExecutionTimeInput: state.maxExecutionTimeInput,
          uniqueIdentifiers: state.uniqueIdentifiers,
          journeyStepDescription: state.journeyStepDescription,
          endpointNodeId: state.selectedEndpointFlowItem?.nodeId,
        }}
        readonly={!!state.viewJourneyInitiated}
        cloning={!!cloneJourneyName}
      />
      <ConfirmErrorModal notifyApiError={state.notifyApiError} confirmErrorHandler={() => confirmErrorHandler(goToPrevPage, dispatch)} />
      <SchedulerModal schedulerModalFlag={state.schedulerModal} closeSchedulerModal={() => setCloseSchedulerModal(dispatch)} journeyName={state.journeyName} journeyStepName={getSchedularStepName()} eventName={getSchedulerEventName()} eventVersion={getSchedulerEventVersion()} journeyVersion={state.journeyVersion} />
      <ActiveStatusEditWarningModal
        showActiveStatusModal={showModalForActiveEdit}
        proceedActiveStatus={() => proceedActiveStatusEditWarningModal(setShowModalForActiveEdit, dispatch)}
        cancelActiveStatus={() => cancelActiveStatusEditWarningModal(setShowModalForActiveEdit, dispatch)}
      />
      <CemCancelWarningModal showCancelWarningModal={showCancelWarningModal} leaveCancelWarningModal={() => leaveCancelWarningModal({ setShowCancelWarningModal, stepDataRef, goToPrevPage, dispatch, designingStep: state.designingStep })} setShowCancelWarningModal={setShowCancelWarningModal} />
      <ApproveWarningModal
        journeyVersion={state.journeyVersion}
        showApproveWarningModal={showApproveWarningModal}
        setShowApproveWarningModal={setShowApproveWarningModal}
        journeyName={state.journeyName}
        goToPrevPage={goToPrevPage}
        journeyComments={state.journeyComments}
      />
      <ReturnJourneyModal showReturnModal={showReturnModal} setShowReturnModal={setShowReturnModal} journeyName={state.journeyName} journeyVersion={state.journeyVersion} goToPrevPage={goToPrevPage} />
      <SendForApproveModal showSendForApprovalModal={showSendForApprovalWarningModal} journeyName={state.journeyName} journeyVersion={state.journeyVersion} setShowSendForApprovalWarningModal={setShowSendForApprovalWarningModal} goToPrevPage={goToPrevPage} />
      <MoveToDraftModal showMoveToDraftModal={showMoveToDraftWarningModal} journeyName={state.journeyName} journeyVersion={state.journeyVersion} setShowMoveToDraftWarningModal={setShowMoveToDraftWarningModal} goToPrevPage={goToPrevPage} />
      <PayloadTransformationModal
        services={state.dropDownServicesPayload}
        show={state.showTransformPayloadModal}
        eventName={state.payloadTransformationEventName}
        eventVersion={state.payloadTransformationEventVersion}
        onClose={() => closeTransformPayloadModal(dispatch)}
        payloadTransformations={state.payloadTransformations}
        onSave={(payloadData: PayloadTransformation[], nodeId: string) => onPayloadSaveHandler(nodeId, payloadData, dispatch, state.flowItems)}
        nodeId={state.payloadTransformationNodeId}
        readOnly={!!state.viewJourneyInitiated}
      />
      <SaveJourneyToDesignCemModal showSaveJourneyToDesignCemModal={state.saveJourneyToDesignCEMModal} closeSaveJourneyModal={() => closeSaveJourneyModal(dispatch)} />
      <ExternalOnSubsequentStepModal show={state.showExternalOnSubsequentStepModal} onDismiss={dismissExternalOnSubsequentStepModalHandler} />
      <ExternalEventConfirmationModal
        show={state.showExternalEventConfirmationModal}
        externalEventConfirmationData={state.externalEventConfirmationData}
        onValueUpdated={(e) => dispatch({
          type: Actions.EXTERNAL_EVENT_CONFIRMATION_SAVE,
          payload: { external: e.external, toNodeId: e.toNodeId, fromNodeId: e.fromNodeId }
        })}
        onClose={() => dispatch({ type: Actions.HIDE_EXTERNAL_EVENT_CONFIRMATION_MODAL })}
        readOnly={state.viewJourneyInitiated}
      />
      <Modal
        modalTitle={translate('journey_builder_component.title_payload_not_found')}
        modalBody={translate('journey_builder_component.msg_add_payload')}
        modalShow={showPayloadInfoModal}
        btnSaveText="OK"
        onSaveModal={handlePayloadInfoModalDismiss}
        onCloseModal={handlePayloadInfoModalDismiss}
        alignToCentre
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="info"
        data-testid="payloadInfoModal"
      />
      {showOrToAndPayloadModal && (
        <Modal
          modalTitle={String(translate('journey_builder_component.title_payload_not_found'))}
          modalBody={String(translate('journey_builder_component.msg_add_payload'))}
          modalShow
          btnSaveText={String(translate('journey_builder_component.btn_continue_switch_to_and'))}
          btnCloseText={String(translate('journey_builder_component.lbl_cancel'))}
          onSaveModal={handleOrToAndPayloadModalAccept}
          onCloseModal={handleOrToAndPayloadModalCancel}
          alignToCentre
          disableCloseBtn={false}
          disableSaveBtn={false}
          type="info"
          data-testid="orToAndPayloadModal"
        />
      )}
      {showOrToAndCondExpModal && (
        <Modal
          modalTitle={String(translate('journey_builder_component.title_switch_to_and'))}
          modalBody={(
            <div>
              <p>{String(translate('journey_builder_component.msg_and_toggle_cond_exp_list_intro'))}</p>
              <ul>
                {pendingCondExpIngressLabels.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
          )}
          modalShow
          btnSaveText={String(translate('journey_builder_component.btn_continue_switch_to_and'))}
          btnCloseText={String(translate('journey_builder_component.lbl_cancel'))}
          onSaveModal={handleOrToAndCondExpModalAccept}
          onCloseModal={handleOrToAndCondExpModalCancel}
          alignToCentre
          disableCloseBtn={false}
          disableSaveBtn={false}
          type="info"
          data-testid="orToAndCondExpClearModal"
        />
      )}
    </>
  );
};

export default JourneyCanvas;
